import React from 'react';
import moment from 'moment';
import { getPlanningDeletionReason } from './planningSchedule';
import { useDrag } from 'react-dnd';
import { parsePlanningTitle, getPlanningManpower } from './planningCalendar';
import { moveBoardEvent, resizeBoardEvent } from './planningBoard';
import PlanningJobTooltip from './PlanningJobTooltip.jsx';

export const BOARD_DRAG_TYPE = 'planning-board-job';

export default function PlanningBoardJob({ segment, machine, shift, canEdit, onOpenJob, onDeleteJob, onMoveJob, onResizeJob, onError }) {
    const { event } = segment;
    const parsed = parsePlanningTitle(event.title);
    const editable = canEdit && event.metricsPending !== true;
    const dragItem = mode => ({ event, machine, mode, sourceTimestamp: segment.visibleStart });
    const [{ dragging }, dragRef] = useDrag(() => ({
        type: BOARD_DRAG_TYPE, canDrag: editable && Boolean(onMoveJob), item: dragItem('move'),
        collect: monitor => ({ dragging: monitor.isDragging() }),
    }), [event, machine, editable, segment.visibleStart, onMoveJob]);
    const [, startRef] = useDrag(() => ({
        type: BOARD_DRAG_TYPE, canDrag: editable && Boolean(onResizeJob), item: dragItem('start'),
    }), [event, machine, editable, onResizeJob]);
    const [, endRef] = useDrag(() => ({
        type: BOARD_DRAG_TYPE, canDrag: editable && Boolean(onResizeJob), item: dragItem('end'),
    }), [event, machine, editable, onResizeJob]);
    const separator = parsed.displayTitle.indexOf(' ');
    const prefix = separator < 0 ? parsed.displayTitle : parsed.displayTitle.slice(0, separator);
    const description = separator < 0 ? '' : parsed.displayTitle.slice(separator + 1);
    const onKeyDown = keyEvent => {
        if (!editable || !keyEvent.altKey) return;
        const offsets = { ArrowUp: -shift.slotMinutes, ArrowDown: shift.slotMinutes, ArrowLeft: -1440, ArrowRight: 1440 };
        const minutes = offsets[keyEvent.key];
        if (!minutes) return;
        keyEvent.preventDefault();
        const edge = keyEvent.shiftKey ? 'end' : keyEvent.ctrlKey ? 'start' : null;
        const target = moment(edge === 'end' ? event.end : event.start).add(minutes, 'minutes').format('YYYY-MM-DD HH:mm:ss');
        try {
            onError('');
            if (edge && onResizeJob) onResizeJob(event, resizeBoardEvent(event, edge, target));
            else if (!edge && onMoveJob) onMoveJob(event, moveBoardEvent(event, target));
        } catch (error) { onError(error.message); }
    };

    return (
        <div
            ref={editable && onMoveJob ? dragRef : undefined}
            className={`planning-board__job${moment(segment.date).day() === 0 && event.ot !== true ? ' is-sunday' : ''}`}
            style={{
                top: `${segment.startMinute / shift.durationMinutes * 100}%`,
                height: `${(segment.endMinute - segment.startMinute) / shift.durationMinutes * 100}%`,
                left: `${segment.lane / segment.columns * 100}%`,
                width: `${100 / segment.columns}%`,
                '--board-job-color': event.bgColor || 'var(--planning-accent)',
                opacity: dragging ? 0.5 : undefined,
            }}
        >
            {editable && onResizeJob && !segment.continuesBefore && <span ref={startRef} className="planning-board__resize planning-board__resize--start" aria-hidden="true" />}
            <PlanningJobTooltip event={event}>
                <button type="button" className="planning-board__open" aria-label={`Open ${parsed.displayTitle}`} onClick={() => onOpenJob && onOpenJob(event)} onKeyDown={onKeyDown}>
                    {segment.continuesBefore && <span className="planning-board__continuation" aria-label="Continues from earlier">↑</span>}
                    <strong>{prefix}</strong>
                    {description && <span>{description}</span>}
                    <strong>({getPlanningManpower(event) ?? '—'}){parsed.closed ? ' CLOSED' : ''}</strong>
                    {segment.continuesAfter && <span className="planning-board__continuation" aria-label="Continues later">↓</span>}
                </button>
            </PlanningJobTooltip>
            {canEdit && onDeleteJob && <button type="button" className="planning-board__delete" disabled={Boolean(getPlanningDeletionReason(event))} title={getPlanningDeletionReason(event) || 'Delete schedule'} aria-label={`Delete ${parsed.displayTitle}`} onClick={() => onDeleteJob(event)}>×</button>}
            {editable && onResizeJob && !segment.continuesAfter && <span ref={endRef} className="planning-board__resize planning-board__resize--end" aria-hidden="true" />}
        </div>
    );
}
